/**
 * AttendanceCalendar — full-month box-grid like Ward & Beds.
 * Renders weekdays Mon-Sun across the top, full month rows of date cells.
 * Color-codes status: present (emerald), on_leave (red), off (slate), holiday (blue), absent (amber).
 */
import { useMemo } from 'react';
import { cn } from '../lib/utils';

const STATUS_STYLE = {
  present:  { box: 'bg-emerald-100 border-emerald-300 text-emerald-700', dot: 'bg-emerald-500',  label: 'Present' },
  on_leave: { box: 'bg-red-100 border-red-300 text-red-700',             dot: 'bg-red-500',      label: 'Leave'   },
  absent:   { box: 'bg-amber-100 border-amber-300 text-amber-700',       dot: 'bg-amber-500',    label: 'Absent'  },
  off:      { box: 'bg-slate-100 border-slate-200 text-slate-500',       dot: 'bg-slate-400',    label: 'Off-day' },
  holiday:  { box: 'bg-blue-100 border-blue-300 text-blue-700',          dot: 'bg-blue-500',     label: 'Holiday' },
};

const AttendanceCalendar = ({ attendance = [], month, year }) => {
  const today = useMemo(() => new Date(), []);
  const _month = month != null ? month : today.getMonth();
  const _year = year != null ? year : today.getFullYear();

  // Map date string → status
  const byDate = useMemo(() => {
    const m = new Map();
    attendance.forEach(a => { if (a?.date) m.set(a.date, a.status); });
    return m;
  }, [attendance]);

  // Build month grid — start from first day of month, pad to weekday Monday-first
  const cells = useMemo(() => {
    const first = new Date(_year, _month, 1);
    const last = new Date(_year, _month + 1, 0).getDate();
    // Monday=0, Sunday=6
    const leading = (first.getDay() + 6) % 7;
    const out = [];
    for (let i = 0; i < leading; i += 1) out.push({ blank: true });
    for (let d = 1; d <= last; d += 1) {
      const ds = `${_year}-${String(_month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dt = new Date(_year, _month, d);
      const isFuture = dt > today;
      let status = byDate.get(ds);
      if (!status && !isFuture) {
        status = dt.getDay() === 0 ? 'off' : 'absent';
      }
      out.push({ date: ds, day: d, weekday: dt.getDay(), status, isFuture, isToday: ds === today.toISOString().slice(0, 10) });
    }
    return out;
  }, [_year, _month, byDate, today]);

  // Aggregate counts
  const counts = useMemo(() => {
    const c = { present: 0, on_leave: 0, absent: 0, off: 0, holiday: 0 };
    cells.forEach(cell => { if (!cell.blank && !cell.isFuture && c[cell.status] != null) c[cell.status] += 1; });
    return c;
  }, [cells]);

  return (
    <div data-testid="attendance-calendar">
      {/* Counts strip */}
      <div className="grid grid-cols-5 gap-1.5 mb-3">
        {(['present', 'on_leave', 'absent', 'off', 'holiday']).map(k => (
          <div key={k} className={cn('rounded-lg border p-2 text-center', STATUS_STYLE[k].box)} data-testid={`att-count-${k}`}>
            <p className="text-base font-bold tabular-nums">{counts[k]}</p>
            <p className="text-[9px] uppercase tracking-wider opacity-80">{STATUS_STYLE[k].label}</p>
          </div>
        ))}
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] text-slate-400 uppercase tracking-wider text-center">
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <div key={d}>{d}</div>)}
      </div>

      {/* Cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (c.blank) return <div key={`b-${i}`} />;
          const style = c.isFuture
            ? 'bg-white border-dashed border-slate-100 text-slate-300'
            : STATUS_STYLE[c.status]?.box || 'bg-slate-100 border-slate-200 text-slate-400';
          return (
            <div
              key={c.date}
              className={cn(
                'aspect-square rounded-md border flex flex-col items-center justify-center text-[11px] font-semibold transition-all',
                style,
                c.isToday && 'ring-2 ring-blue-500 ring-offset-1',
              )}
              title={`${c.date} · ${c.isFuture ? '—' : (STATUS_STYLE[c.status]?.label || c.status)}`}
              data-testid={`att-cell-${c.date}`}
            >
              <span>{c.day}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AttendanceCalendar;
